type EventKey = string | symbol;

type EventToolType = Record<EventKey, (...args: any[]) => any>;

export class EventTool<T extends EventToolType> {
  static forwardEvent<
    T1 extends EventToolType,
    T2 extends EventToolType,
    EvtType extends (keyof T1 | [keyof T1, keyof T2])[]
  >(
    from: { on: EventTool<T1>["on"] },
    to: { emit: EventTool<T2>["emit"] },
    evtTypes: EvtType
  ): () => void {
    const removeHandlers = evtTypes.map((evtType) => {
      const [fromEvtType, toEvtType] = (
        Array.isArray(evtType) ? evtType : [evtType, evtType]
      ) as [keyof T1, keyof T2];

      // @ts-expect-error
      return from.on(fromEvtType, (...args) => {
        // @ts-expect-error
        to.emit(toEvtType, ...args);
      });
    });
    return () => {
      removeHandlers.forEach((fn) => fn());
    };
  }

  #listeners = new Map<keyof T, Set<T[keyof T]>>();

  on = <Type extends keyof T>(type: Type, listener: T[Type]): (() => void) => {
    const handlers = this.#listeners.get(type) ?? new Set<T[keyof T]>();
    handlers.add(listener);

    if (!this.#listeners.has(type)) {
      this.#listeners.set(type, handlers);
    }

    return () => {
      handlers.delete(listener);
      if (handlers.size === 0) {
        this.#listeners.delete(type);
      }
    };
  };

  once = <Type extends keyof T>(
    type: Type,
    listener: T[Type]
  ): (() => void) => {
    // @ts-ignore
    const off = this.on(type, (...args) => {
      off();
      listener(...args);
    });

    return off;
  };

  emit = <Type extends keyof T>(
    type: Type,
    ...args: Type extends string
      ? T[Type] extends (...args: any[]) => any
        ? Parameters<T[Type]>
        : never
      : never
  ): void => {
    const handlers = this.#listeners.get(type);
    if (handlers == null) return;

    handlers.forEach((handler) => handler(...args));
  };

  destroy(): void {
    this.#listeners.clear();
  }
}
